/**
 * Begins a stream with rev.ai using the AudioContext from the browser. Stream will continue until the websocket
 * connection is closed. Follows the protocol specficied in our documentation:
 * https://docs.rev.ai/api/streaming/
 */

import { io } from 'socket.io-client';
import {
  assessmentRevAiPartialResponseSlice,
  assessmentRevAiResponseSlice,
  diagnosticsRevAiPartialResponseSlice,
  diagnosticsRevAiResponseSlice,
  physicalExamRevAiPartialResponseSlice,
  physicalExamRevAiResponseSlice,
  revaiPartialResponse,
  revaiResponse,
  startAssessmentPlanListening,
  startDiagnosticsListening,
  startListening,
  startPhysicalExamListening,
  startVoiceRecorder,
} from '../store/slices/careConnect.slice';
import store from 'src/store';

let audioContext, socket, input, micStream, scriptNode;

let url = import.meta.env.VITE_API_BASE_URL + '/speech_to_text';

export function doStream(type) {
  audioContext = new (window.AudioContext || window.WebkitAudioContext)();
  socket = io(url, { path: '/care/socket.io/', withCredentials: true });

  socket.on('disconnect', () => {
    console.warn('websocket closed due to server disconnection');
    onClose();
  });

  socket.on('response', (data) => {
    onMessage(data, type);
  });

  socket.on('error', (error) => {
    console.error(error);
  });

  onOpen();
}

/**
 * Gracefully ends the streaming connection with rev.ai. Signals and end of stream before closing and closes the
 * browser's AudioContext
 */
export function endStream() {
  store.dispatch(startListening(false));

  window.isMicOn = false;

  if (scriptNode) {
    scriptNode.removeEventListener('audioprocess', processAudioEvent);
    scriptNode.disconnect();
  }

  if (input) {
    input.disconnect();
  }

  if (micStream) {
    micStream.getTracks().forEach((track) => track.stop());
  }

  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close();
  }

  if (socket) {
    socket.close();
  }
}

/**
 * Updates the display and creates the link from the AudioContext and the websocket connection to rev.ai
 * @param {Event} event
 */
function onOpen() {
  navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
    micStream = stream;
    audioContext.suspend();
    scriptNode = audioContext.createScriptProcessor(4096, 1, 1);
    input = audioContext.createMediaStreamSource(micStream);
    scriptNode.addEventListener('audioprocess', (event) =>
      processAudioEvent(event)
    );
    input.connect(scriptNode);
    scriptNode.connect(audioContext.destination);
    audioContext.resume();

    window.isMicOn = true;
  });
}

/**
 * Displays the close reason and code on the webpage
 * @param {CloseEvent} event
 */
function onClose() {
  endStream();
}

/**
 * Handles messages received from the API according to our protocol
 * https://docs.rev.ai/api/streaming/responses/
 * @param {MessageEvent} event
 */
function onMessage(event, type) {
  switch (event.type) {
    case 'connected':
      store.dispatch(startVoiceRecorder(false));
      store.dispatch(
        type === 'intake_summary'
          ? startListening(true)
          : type === 'physical_exam'
            ? startPhysicalExamListening(true)
            : type === 'diagnostic_imaging'
              ? startDiagnosticsListening(true)
              : type === 'assesment_plan'
                ? startAssessmentPlanListening(true)
                : null
      );
      break;
    case 'partial':
      store.dispatch(
        type === 'intake_summary'
          ? revaiPartialResponse(parseResponse(event))
          : type === 'physical_exam'
            ? physicalExamRevAiPartialResponseSlice(parseResponse(event))
            : type === 'diagnostic_imaging'
              ? diagnosticsRevAiPartialResponseSlice(parseResponse(event))
              : type === 'assesment_plan'
                ? assessmentRevAiPartialResponseSlice(parseResponse(event))
                : null
      );
      break;
    case 'final':
      store.dispatch(
        type === 'intake_summary'
          ? revaiResponse(parseResponse(event))
          : type === 'physical_exam'
            ? physicalExamRevAiResponseSlice(parseResponse(event))
            : type === 'diagnostic_imaging'
              ? diagnosticsRevAiResponseSlice(parseResponse(event))
              : type === 'assesment_plan'
                ? assessmentRevAiResponseSlice(parseResponse(event))
                : null
      );
      break;
    default:
      // We expect all messages from the API to be one of these types
      console.error('onMessage - Received unexpected message');
      break;
  }
}

/**
 * Transform an audio processing event into a form suitable to be sent to the API. (S16LE or Signed 16 bit Little Edian).
 * Then send.
 * @param {AudioProcessingEvent} e
 */
function processAudioEvent(e) {
  if (
    audioContext.state === 'suspended' ||
    audioContext.state === 'closed' ||
    !socket
  ) {
    return;
  }

  let inputData = e.inputBuffer.getChannelData(0);

  // The samples are floats in range [-1, 1]. Convert to PCM16le.
  let output = new DataView(new ArrayBuffer(inputData.length * 2));
  for (let i = 0; i < inputData.length; i++) {
    let multiplier = inputData[i] < 0 ? 0x8000 : 0x7fff; // 16-bit signed range is -32768 to 32767
    output.setInt16(i * 2, (inputData[i] * multiplier) | 0, true); // index, value, little edian
  }

  let intData = new Int16Array(output.buffer);
  let index = intData.length;

  socket.emit(
    'audio',
    JSON.stringify({
      audio: Object.values(intData.slice(0, index + 1)),
    })
  );
}

function parseResponse(response) {
  var message = '';
  for (var i = 0; i < response.elements.length; i++) {
    message +=
      response.type == 'final'
        ? response.elements[i].value
        : `${response.elements[i].value} `;
  }
  return message;
}
